import { DeferredPromise } from "DeferredPromise";
import global from "Global";
import log from "Log";
import { loadNativeBridgeAsync } from "ModuleLoader";
import type { NativeBridgeProvider } from "NativeBridgeProvider";
import { SessionType } from "SessionType";
import shortcutService from "ShortcutService";
import $ from "jquery";

class NativeBridge {
  commands = {
    selectPictureCommand: "selectPicture",
    startTrackingCommand: "startTracking",
    stopTrackingCommand: "stopTracking",
    isScrollableCommand: "nativeIsScrollable",
    webAppReadyCommand: "WebAppReady",
    setLockTextZoomCommand: "setLockTextZoom",
    onUserLogOffCommand: "onUserLogOff",
  };
  callbackDeferreds: { [id: string]: DeferredPromise<unknown> | null } = {};
  defaultOptions = {
    callback: "glowNativeBridgePerformCommandCallback",
    errorcallback: "glowNativeBridgeErrorCallback",
    scanEventKey: "glowScanEvent",
    setupKey: "glowSetupNativeBridge", // This must match the code in the Android and Windows Phone clients
    initializedKey: "glowNativeBridgeIsInitialized", // This must match the code in the Windows Phone client
    setupPromiseKey: "glowNativeBridgeSetupPromise",
    setupPromiseResolveCallbackKey: "glowNativeBridgeSetupPromiseResolveCallback",
  };
  providers: { [key: string]: string } = {
    Android: "AndroidNativeBridgeProvider",
    Electron: "ElectronNativeBridgeProvider",
  };
  provider?: NativeBridgeProvider | null = null;
  providerName = null;
  initializationObject: { [key: string]: unknown } = {};
  nativeClientVersion: string | null = null;
  nativeClientOsBuildVersion: string | null = null;
  nativeClientSerialNumber: string | null = null;
  nativeClientDeviceType: string | null = null;
  nativeClientDeviceModel: string | null = null;
  enableGPSTracking: boolean | null = null;
  lastTimeLocationRecorded: string | null = null;

  private _isLoadingNativeBridge = false;
  private _webAppIsReadyDeferred = new DeferredPromise();
  private _nativeClientSessionType: string | null = null;

  isOpen(): boolean {
    return !!this.provider;
  }

  isScanProvider(): boolean {
    return !!(this.isOpen() && this.provider?.isScanProvider && this.provider?.isScanProvider());
  }

  deferredForCommand(bridge: NativeBridge, command: string): DeferredPromise<unknown> | null {
    return bridge.callbackDeferreds[command];
  }

  clearDeferredForCommand(bridge: NativeBridge, command: string): void {
    bridge.callbackDeferreds[command] = null;
  }

  performCommandAsync(command: string, parameters?: unknown): Promise<unknown> {
    if (this.isOpen()) {
      const callbackDeferred = this.newDeferredForCommand(command);

      this.provider && this.provider.pushMessage(command, parameters, this.defaultOptions);

      return callbackDeferred.promise;
    }

    return Promise.resolve();
  }

  errorCallback(command: string, argument: unknown): void {
    const callbackDeferred = this.deferredForCommand(this, command);
    if (callbackDeferred) {
      callbackDeferred.reject(argument);
      log.error(`An error occurred while running command "${command}" through native bridge: ${argument}`);
      this.clearDeferredForCommand(this, command);
    }
  }

  performCommandCallback(command: string, argument: unknown): void {
    const callbackDeferred = this.deferredForCommand(this, command);
    if (callbackDeferred) {
      callbackDeferred.resolve(argument);
      this.clearDeferredForCommand(this, command);
    }
  }

  scanEvent(
    data: string | number | string[] | ((this: HTMLElement, index: number, value: string) => string),
    type: string,
  ): void {
    const $focused = $(":focus");
    $focused.trigger("blur");

    let $element = shortcutService.trigger("ctrl+[");

    if (!$element) {
      if ($focused.length) {
        $element = $focused;
      }
    }

    if ($element && $element.length && !$element.attr("readonly")) {
      data = this.sanitize(data);

      if ($element.attr("data-settable")) {
        $element.component("setValue", data);
      } else {
        $element.val(data);
        $element.trigger("change");
      }
      this.setLastScannedElement($element);

      //We need to check for both because Zebra reports it as EAN128 but Datalogic reports it as GS1_128
      if (type === "EAN128" || type === "GS1_128") {
        shortcutService.trigger("ctrl+]");
      } else {
        shortcutService.trigger("ctrl+\\");
      }
    }
  }

  private sanitize(
    data: string | number | string[] | ((this: HTMLElement, index: number, value: string) => string),
  ): typeof data {
    return typeof data === "string" ? data.replace(/\0/g, "") : data;
  }

  private setLastScannedElement($element: JQuery<HTMLElement>): void {
    $("[data-last-scanned]").removeAttr("data-last-scanned");
    $element.attr("data-last-scanned", "true");
  }

  onCommandCompleted(command: string, argument: unknown): void {
    this.performCommandCallback(command, argument);
  }

  onError(command: string, argument: unknown): void {
    this.errorCallback(command, argument);
  }

  setupForClient(
    client: string,
    version: string,
    osBuildVersion: string,
    serialNumber: string,
    deviceType: string,
    deviceModel: string,
    sessionType: string,
  ): void;
  setupForClient(
    client: string,
    version: string,
    osBuildVersion: string,
    serialNumber: string,
    deviceType: string,
    deviceModel: string,
    gpsTrackingEnabled: boolean,
    lastTimeLocationRecordedFromCache: string,
    sessionType: string,
  ): void;
  setupForClient(
    client: string,
    version: string,
    osBuildVersion: string,
    serialNumber: string,
    deviceType: string,
    deviceModel: string,
    gpsTrackingEnabledOrSessionType: boolean | string,
    lastTimeLocationRecordedFromCache?: string,
    sessionType?: string,
  ): void {
    const providerName = this.providers[client];
    if (providerName) {
      if (this.isLoadingOrLoaded()) {
        return;
      }
      this._isLoadingNativeBridge = true;

      if (typeof gpsTrackingEnabledOrSessionType === "boolean") {
        this.enableGPSTracking = gpsTrackingEnabledOrSessionType;
        this.lastTimeLocationRecorded = lastTimeLocationRecordedFromCache ?? null;
        this._nativeClientSessionType = sessionType ?? null;
      } else {
        this._nativeClientSessionType = gpsTrackingEnabledOrSessionType;
      }

      this.nativeClientVersion = version;
      this.nativeClientOsBuildVersion = osBuildVersion;
      this.nativeClientSerialNumber = serialNumber;
      this.nativeClientDeviceType = deviceType;
      this.nativeClientDeviceModel = deviceModel;
      this.loadAndSetupNativeBridgeAsync(providerName);
      this.invokeIfFunction(this.initializationObject[this.defaultOptions.setupPromiseResolveCallbackKey]);
    }
  }

  private async loadAndSetupNativeBridgeAsync(providerName: string): Promise<void> {
    try {
      const p = await loadNativeBridgeAsync(providerName);
      const provider = p as NativeBridgeProvider;
      if (this.initializationObject) {
        this.initializationObject[this.defaultOptions.initializedKey] = true;
      }
      this._isLoadingNativeBridge = false;
      this.provider = provider;
      await this.setupNativeBridgeAsync(provider);
    } finally {
      this._isLoadingNativeBridge = false;
    }
  }

  private async setupNativeBridgeAsync(provider: NativeBridgeProvider): Promise<void> {
    await this._webAppIsReadyDeferred.promise;
    if (provider.setupForClientAsync) {
      await provider.setupForClientAsync();
    }
    await Promise.all([
      this.performCommandAsync(this.commands.webAppReadyCommand),
      this.performCommandAsync(this.commands.setLockTextZoomCommand, global.materialDesign),
    ]);
  }

  getSessionType(): SessionType {
    const sessionType = SessionType[this._nativeClientSessionType as keyof typeof SessionType];
    return sessionType ?? this._nativeClientSessionType;
  }

  webAppIsReady(): void {
    this._webAppIsReadyDeferred.resolve(null);
  }

  initialize(obj: { [key: string]: unknown }): void {
    const setupKey = this.defaultOptions.setupKey;

    this.initializationObject = obj;
    this.initializationObject[setupKey] = this.setupForClient.bind(this);

    this.initializationObject[this.defaultOptions.callback] = this.performCommandCallback.bind(this);
    this.initializationObject[this.defaultOptions.errorcallback] = this.errorCallback.bind(this);
    this.initializationObject[this.defaultOptions.scanEventKey] = this.scanEvent.bind(this);

    this._isLoadingNativeBridge = false;
  }

  ensureSupportedVersion(): boolean {
    if (!this.provider || !this.nativeClientVersion) {
      return true;
    }

    return this.provider.ensureSupportedVersion(this.nativeClientVersion);
  }

  async waitForInitAsync(): Promise<void> {
    await this.initializationObject[this.defaultOptions.setupPromiseKey];
  }

  private invokeIfFunction(value: unknown): void {
    if (typeof value === "function") {
      value();
    }
  }

  private newDeferredForCommand(command: string): DeferredPromise<unknown> {
    const existingDeferred = this.callbackDeferreds[command];
    if (existingDeferred) {
      existingDeferred.reject(`Attempted to run ${command} twice`);
      this.callbackDeferreds[command] = null;
    }

    const deferred = new DeferredPromise();
    this.callbackDeferreds[command] = deferred;
    return deferred;
  }

  private isLoadingOrLoaded(): boolean {
    return !!(
      this._isLoadingNativeBridge ||
      (this.initializationObject && this.initializationObject[this.defaultOptions.initializedKey])
    );
  }
}

export default new NativeBridge();
